import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Card, CardContent, Grid, Button, CircularProgress } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import PersonIcon from "@mui/icons-material/Person";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import MapComponent from "../MapComponent";
import { IOrderListDTO, IOrderViewResponseDTO } from "../../interfaces/Orders";
import { getViewOrder } from "../../services/Orders";
import { formatDisplayDate } from "../../util/Util";
import BarcodeDisplay from "../common/BarcodeDisplay";
import QRDisplay from "../common/QRDisplay";

interface ViewOrderDetailsProps {
  orderData: IOrderListDTO | null;
  onBack: () => void;
}

const ViewOrderDetails = ({ orderData,onBack }: ViewOrderDetailsProps) => {
    const [orderDetails, setorderDetails] = useState<IOrderViewResponseDTO | null>(null);
    const [loading, setLoading] = useState(false);
    const [showQR, setShowQR] = useState(false);

  const origin = orderDetails?.warehouseLatitude && orderDetails?.warehouseLongitude
    ? { lat: orderDetails.warehouseLatitude, lng: orderDetails.warehouseLongitude } : undefined;
  const destination = orderDetails?.customerLatitude && orderDetails?.customerLongitude
    ? { lat: orderDetails.customerLatitude, lng: orderDetails.customerLongitude } : undefined;

  const routeDetails = [
    { name: "Warehouse", address: orderDetails?.warehouseAddress || "", type: "start" },
    { name: orderDetails?.customerName || "Customer", address: orderDetails?.address || "", type: "end" },
  ];

  useEffect(() => {
    if (!orderData?.orderId) return;
    (async () => {
      try {
        setLoading(true);
        const res = await getViewOrder(orderData.orderId);
        if (res) {
          setorderDetails(res as IOrderViewResponseDTO);
        }
      } catch {
        setorderDetails(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [orderData?.orderId]);
  return (
    <Box sx={{ width: "100%", height: "90vh", display: "flex", flexDirection: "column", backgroundColor: "#F5F5F5" }}>
      <Box sx={{p:1}}>
        <Button startIcon={<ArrowBackIosNewIcon sx={{ fontSize: "14px" }} />} onClick={onBack} sx={{ textTransform: "none", color: "text.primary", p: 0, minWidth: "auto", fontSize: "14px" }}>
          Back
        </Button>
      </Box>

      <Paper sx={{ flex: 1, m: 1, mt: 0, display: "flex", flexDirection: "column", borderRadius: "8px", overflow: "hidden" }}>
        <Box sx={{ px: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>Order details</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>Here are the full order details</Typography>
          </Box>
          {orderDetails?.packageBarcode && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <BarcodeDisplay
                value={orderDetails.packageBarcode}
                showPrint={true}
                showLabel={true}
                width={1.5}
                height={40}
                fontSize={10}
                margin={0}
                showPrintText={true}
                labelStyle={{ color: "grey.500", fontSize: "12px", fontWeight: 400 }}
                showRequired={false}
              />
              <Button
                onClick={() => setShowQR(true)}
                sx={{ color: "primary.main", textTransform: "none", fontSize: "13px", fontWeight: 500, minWidth: "auto", p: 0 }}
              >
                Generate QR
              </Button>
            </Box>
          )}
        </Box>

        {orderDetails?.packageBarcode && (
          <QRDisplay
            open={showQR}
            onClose={() => setShowQR(false)}
            value={orderDetails.packageBarcode}
            label="Package QR Code"
          />
        )}

        <Box sx={{ flex: 1, overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
          <Box sx={{ p:2}}>
            <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
              <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" fontWeight={600} mb={1}>Customer Details</Typography>
                <Card sx={{ backgroundColor: "background.paper", boxShadow: 1, display: "flex", flexDirection: "column", flex: 1 }}>
                  <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1}}>
                      <Box sx={{ backgroundColor: "primary.light", border: "2px solid", borderColor: "primary.main", borderRadius: 1, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", mr: 2 }}>
                        <PersonIcon sx={{ color: "primary.main" }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{orderDetails?.customerName || ''}</Typography>
                        <Typography variant="caption" color="grey.primary">Customer</Typography>
                      </Box>
                    </Box>
                    <Grid container spacing={2} sx={{ flex: 1 }}>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="grey.primary" sx={{ fontSize: "10px" }}>Phone number</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{orderDetails?.phone || ''}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="grey.primary" sx={{ fontSize: "10px" }}>Delivery date</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{formatDisplayDate(orderDetails?.deliveryDate || '')}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="grey.primary" sx={{ fontSize: "10px" }}>Address</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{orderDetails?.address || ''}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" fontWeight={600} mb={1}>Shipping Details</Typography>
                <Card sx={{ backgroundColor: "background.paper", boxShadow: 1, display: "flex", flexDirection: "column", flex: 1 }}>
                  <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Box sx={{ backgroundColor: "primary.light", border: "2px solid", borderColor: "primary.main", borderRadius: 1, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", mr: 2 }}>
                        <LocalShippingIcon sx={{ color: "primary.main" }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{orderDetails?.partnerName || ''}</Typography>
                        <Typography variant="caption" color="grey.primary">Delivery Partner</Typography>
                      </Box>
                    </Box>
                    <Grid container spacing={2} sx={{ flex: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="grey.primary" sx={{ fontSize: "10px" }}>Phone number</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{orderDetails?.partnerPhone || ''}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="grey.primary" sx={{ fontSize: "10px" }}>Out for Delivery Time</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{formatDisplayDate(orderDetails?.outForDeliveryTime || '')}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, alignItems: "start" }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="h6" fontWeight={600} mb={1}>Package Items</Typography>
                    <Paper sx={{ p: 2, backgroundColor: "background.default", borderRadius: "8px", boxShadow: "none" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {orderDetails?.items?.map((item) => (
                          <Box key={item.orderItemId} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: "8px", backgroundColor: item.packed ? "success.light" : "#F9FAFB", border: "1px solid", borderColor: item.packed ? "rgba(0, 178, 33, 0.2)" : "rgba(74,85,101,0.15)" }}>
                            <Box sx={{ width: 45, height: 45, borderRadius: "6px", backgroundColor: "#E5E7EB", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <Box component="img" src={item.imageUrl || "/placeholder.png"} alt={item.itemName} sx={{ width: 35, height: 35, objectFit: "contain" }} onError={(e: any) => { e.target.style.display = "none"; }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ fontSize: "13px" }}>{item.itemName}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>{item.barcode}</Typography>
                            </Box>
                            {item.packed ? (
                              <CheckCircleIcon sx={{ color: "success.main", fontSize: "22px" }} />
                            ) : (
                              <RadioButtonUncheckedIcon sx={{ color: "grey.primary", fontSize: "22px" }} />
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="h6" fontWeight={600} mb={1}>Map Overview</Typography>
                    <Box sx={{ width: "100%", height: "300px", borderRadius: "8px", overflow: "hidden", mb: 2 }}>
                      <MapComponent height="100%" zoom={12} origin={origin} destination={destination} />
                    </Box>

                    <Typography variant="subtitle1" fontWeight={600}>Route Details</Typography>
                    <Paper sx={{ p: 2, backgroundColor: "background.default", borderRadius: "8px", boxShadow: "none" }}>
                      <Box sx={{ position: "relative" }}>
                        {routeDetails.map((route, index) => (
                          <Box key={index} sx={{ display: "flex", gap: 2, pb: index < routeDetails.length - 1 ? 3 : 0 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
                              <Box sx={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: "white", border: "3px solid", borderColor: route.type === "start" ? "primary.main" : "error.main", zIndex: 1 }} />
                              {index < routeDetails.length - 1 && <Box sx={{ width: 3, flex: 1, backgroundColor: "primary.main", position: "absolute", top: 24, bottom: -24 }} />}
                            </Box>
                            <Box sx={{ flex: 1, pb: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ fontSize: "13px" }}>{route.name}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px", display: "block" }}>{route.address}</Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ViewOrderDetails;
